<template>
  <div
    :class="[
      $style['navi-icon'],
      isFavoritesDialog ? $style.favorites : null,
      renderHorizontally && !isSNSearchDialog ? $style.horizontally : null,
    ]"
    @mouseover="setHovered(true)"
    @mouseout="setHovered(false)"
  >
    <component
      :is="getIconItemWrapperType"
      :class="`${getIconItemWrapperType}`"
      :link-tiles="linkTiles"
    >
      <component
        :is="componentType"
        :id="id"
        :class="itemClasses"
        :link="link"
        :disabled="disabled"
        :target="target"
        :title="title"
        @show-notify="setNotificationState"
        @set-icon-count="setIconCount"
      >
        <IconSelect
          :class="$style['item-icon']"
          :icon="icon"
          :height="defaultIconSize"
          :width="defaultIconSize"
        />
        <NavigationIconNotification v-if="notify" :notify="notify" />
        <span v-if="showIconCount" :class="$style.iconCount">{{ iconCount }}</span>
        <span
          v-if="title && !hideTitle"
          :class="[
            $style.title,
            { [$style.show]: (hovered && !isSmWidth) || (renderHorizontally && !isSNSearchDialog) },
          ]"
          >{{ title }}</span
        >
      </component>
    </component>
  </div>
</template>

<script>
import { isUndefined } from 'lodash-es'
import { mapGetters } from 'vuex'

import { CONSTANTS as MESSAGE_CONSTANTS } from '@/js/helper/message-center-constants.js'
import API_CONSTANTS from '@/js/helper/api-constants.js'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'

import NavigationItemMixin from './mixins/navigation-item-mixin.js'

import { STORE_MODULES } from '@/js/plugins/store/modules/aimeos/actions/std-imports'

import NavigationComponents from './components/navigation-routing-components.js'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import NavigationIconNotification from '@/js/components/Navigation/NavigationIconNotification.vue'
import BaseIconWrapper from '@/js/components/Shared/Navigation/BaseIconWrapper.vue'
import SubmenuIconWrapper from '@/js/components/Shared/Navigation/SubmenuIconWrapper.vue'

export default {
  name: 'NavigationIconItem',
  components: {
    IconSelect,
    NavigationIconNotification,
    BaseIconWrapper,
    SubmenuIconWrapper,
    ...NavigationComponents,
  },
  mixins: [NavigationItemMixin],
  props: {
    renderHorizontally: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      renderComponent: true,
      defaultIconSize: 34,
      hovered: false,
      notify: false,
      iconCount: undefined,
      messageStatusInterval: MESSAGE_CONSTANTS.MESSAGE_STATUS_INTERVAL,
      messagesString: API_CONSTANTS.MESSAGE.MESSAGES,
    }
  },
  computed: {
    ...mapGetters(STORE_MODULES.APP, {
      isSmWidth: APP_GETTERS.IS_SM_WIDTH,
    }),
    itemClasses() {
      return ['d-flex flex-column align-center', this.$style.item]
    },
    isFavoritesDialog() {
      return this.componentType.indexOf('Favorites') === 0
    },
    isSNSearchDialog() {
      return this.componentType.indexOf('NavigationSerialNumberSearch') === 0
    },
    showIconCount() {
      return !isUndefined(this.iconCount)
    },
    getIconItemWrapperType() {
      return this.linkTiles?.length ? 'SubmenuIconWrapper' : 'BaseIconWrapper'
    },
  },
  methods: {
    setHovered(state) {
      this.hovered = state
    },
    setNotificationState(state) {
      this.notify = state
    },
    setIconCount(count) {
      this.iconCount = count
    },
  },
}
</script>

<style lang="scss" module>
.item,
.title {
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

.navi-icon {
  margin: 0 !important;

  :global(.v-btn) {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      width: 3.75rem !important; /* same size with other flex navigation points */
    }
    min-width: 0 !important;
  }

  a.item {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      flex-direction: column !important;
      align-items: unset;
      justify-content: inherit !important;
      width: 3.75rem;
      min-width: auto !important;
      height: unset !important;
    }
    position: relative;

    flex-direction: row !important;
    align-items: center;
    justify-content: center !important;

    width: 100%;
    min-width: 55px !important;

    height: 3rem !important; /* same height to other navigation points */

    cursor: pointer;

    .title {
      @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
        margin-top: 0.5rem !important;

        color: var(--black);
      }
      margin-top: 0 !important;
      margin-left: 0;

      color: rgba(0, 0, 0, 0.87);
    }

    :global(.v-btn__content) {
      @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
        flex-direction: column;
        justify-content: inherit !important;
        width: unset;
      }
      flex-direction: row;
      align-items: center;
      justify-content: flex-start !important;
      width: 100%;

      .title {
        @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
          margin-top: 0.5rem !important;

          color: var(--black);
        }
        margin-top: 0 !important;
        margin-left: 0;

        color: rgba(0, 0, 0, 0.87);
      }
    }

    &:hover {
      .iconCount {
        @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
          bottom: 20px;
        }
      }

      & > div {
        top: 30%;
      }

      & > span {
        & > div {
          top: 30%;
        }
      }
    }
  }
}

.item-icon {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: block !important;

    color: var(--primary);
  }
  display: none !important;

  color: var(--black);
}

.favorites {
  min-width: 55px !important;

  .title {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      display: inline-block;
      min-width: auto !important;
    }
    display: none;
  }

  .item-icon {
    display: block !important;

    path {
      @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
        stroke-width: 1.5;
      }
      stroke-width: 1;

      stroke: var(--primary);
    }
  }
}

.iconCount {
  position: absolute;
  right: 5px;
  bottom: 5px;

  width: 15px;
  height: 15px;

  color: var(--white);
  font-size: 0.7rem;
  line-height: 15px;
  text-align: center;

  background-color: var(--blue);
  border-radius: 50%;
}

.title {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    height: 0;
    margin-top: 0.5rem !important;
    margin-left: 0;

    font-size: var(--font-sm);

    line-height: 1;
    white-space: nowrap;
    word-break: keep-all;

    opacity: 0;
  }

  @media screen and (max-width: map-get($grid-breakpoints, 'md')) {
    margin-top: 0 !important;
  }

  height: auto;
  margin-left: 0.5rem;

  color: var(--black);
  font-size: 15px;

  opacity: 1;
}

.show {
  height: auto;
  margin-top: 0.5rem;

  opacity: 1;
}

.horizontally {
  padding-left: 15px;

  :global(.v-btn__content) {
    @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
      flex-direction: row !important;
    }
  }

  .show {
    padding-left: 5px;
  }
}
</style>
